"use client";

import { c as _c } from "react/compiler-runtime";
import { PlayOne } from "@icon-park/svg";
import { Icon } from "@justhome/react-ui";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useState, type ComponentType } from "react";
import type { YouTubeProps } from "react-youtube";

// Dynamic, so neither the player nor Google's iframe API is fetched while the still is all
// that is on screen. Together with mounting it only after the click, that is what keeps the
// facade a facade.
const YouTube = dynamic(() => import("react-youtube").then(mod => mod.default as ComponentType<YouTubeProps>));
export interface VideoFacadeProps {
  videoId: string;
  playLabel: string;
  poster?: {
    url: string;
    alt: string;
    width?: number;
    height?: number;
  };
}
const SIZES = "(min-width: 960px) 48vw, 100vw";
const OPTIONS: YouTubeProps["opts"] = {
  width: "100%",
  height: "100%",
  // Passed straight through to `YT.Player`, so the player itself is the no-cookie one.
  // Google's API script is not covered by it and still comes from youtube.com — but only
  // once someone has pressed play.
  host: "https://www.youtube-nocookie.com",
  // These belong under playerVars. At the top level they are silently ignored, which is why
  // the older Youtube component has to call playVideo() from onReady to make up for it.
  playerVars: {
    autoplay: 1,
    rel: 0
  }
};

/**
 * The player is built on the click, never before it. Embedded outright, YouTube sets its
 * cookies and hands Google the visitor's address before anyone has agreed to anything — two
 * centimetres above a GDPR seal.
 *
 * Without a poster the frame stays dark rather than falling back to `i3.ytimg.com`: that
 * thumbnail is itself a call to Google and would leave the facade doing nothing.
 */
const VideoFacade = t0 => {
  const $ = _c(5);
  const {
    videoId,
    playLabel,
    poster
  } = t0;
  const [playing, setPlaying] = useState(false);
  let t1;
  if ($[0] !== playLabel || $[1] !== playing || $[2] !== poster || $[3] !== videoId) {
    t1 = <div className="relative w-full aspect-[16/9] rounded-m overflow-hidden bg-neutral-675">{playing ? <YouTube videoId={videoId} title={playLabel} opts={OPTIONS} className="absolute inset-none w-full h-full" iframeClassName="w-full h-full" /> : <button type="button" onClick={() => setPlaying(true)} aria-label={playLabel} className="group absolute inset-none w-full h-full p-none border-none bg-transparent cursor-pointer">{poster && <Image src={poster.url} alt={poster.alt} fill={true} sizes={SIZES} priority={true} className="object-cover" />}<span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[72px] h-[72px] rounded-full group-hover:bg-primary-800 group-hover:text-neutral-0 bg-neutral-0 flex items-center justify-center transition-colors"><Icon icon={PlayOne} theme="filled" size={24} /></span></button>}</div>;
    $[0] = playLabel;
    $[1] = playing;
    $[2] = poster;
    $[3] = videoId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};
export default VideoFacade;